import React, { useCallback, useState } from 'react'
import { FlatList, Pressable, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { useRouter } from 'expo-router'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { translate } from '../../i18n'
import { LoungeAvatar } from '../../components/LoungeAvatar'
import { LoungeFullModal } from './LoungeFullModal'
import { LoungeCodeCard } from './components/LoungeCodeCard'
import { isConvexErrorWithCode } from '../../utils/errors/convex'
import { LOUNGE_FULL_ERROR_CODE } from '@ppwr/convex/lounges/capacity'
import { INVALID_JOIN_CODE_ERROR_CODE } from '@ppwr/convex/lounges/errors'

type LoungeItem = {
  _id: string
  name: string
  branding: { logoThumbS3Key?: string; avatar?: any; primaryColor?: string }
  onlineMemberCount: number
  role: string
}

function LoungeCard({ lounge, onPress }: { lounge: LoungeItem; onPress: () => void }) {
  return (
    <Pressable style={styles.loungeCard} onPress={onPress}>
      <LoungeAvatar
        name={lounge.name}
        branding={lounge.branding}
        size={40}
        backgroundColor={lounge.branding.primaryColor}
      />
      <View style={styles.loungeCardDetails}>
        <DS.Text variant="Heading 5/Medium" color="Brand/Maroon" numberOfLines={1}>
          {lounge.name}
        </DS.Text>
        <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
          {translate('lounges.onlineCount', { count: lounge.onlineMemberCount })}
        </DS.Text>
      </View>
      <DS.Icon name="Arrow---right" color="Global/Black" size={20} />
    </Pressable>
  )
}

/**
 * Thin state-managing wrapper around <LoungeCodeCard mode="join">.
 *
 * Owns the controlled input value, the join mutation, error state and the
 * "lounge full" modal. The visual card itself lives in `LoungeCodeCard`,
 * which is shared with the lobby's share-code variant.
 */
function JoinALoungeSection() {
  const router = useRouter()
  const {
    analytics: { setEvent },
    session: { user, isTeacher, isAdmin }
  } = useStore()
  const userId = user?.auth0Id ?? ''
  const mongoUserId = user?._id?.toString()
  const userName = user?.name ?? 'Player'

  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [joining, setJoining] = useState(false)
  const [loungeFullModalVisible, setLoungeFullModalVisible] = useState(false)

  const joinMutation = useMutation(api.lounges.mutations.joinLoungeByCode)

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: join + Convex error branches
  const handleJoin = useCallback(async () => {
    if (!code.trim() || !userId || !mongoUserId) return
    setError(null)
    setJoining(true)
    try {
      const result = await joinMutation({
        joinCode: code.toUpperCase(),
        userId,
        userName,
        mongoUserId
      })
      setEvent(events.lounge.join_lounge_submitted, {
        userId,
        loungeId: result.loungeId ?? undefined,
        joinCode: code.toUpperCase(),
        isTeacher,
        isAdmin
      })
      router.replace({ pathname: '/lounges/[loungeId]', params: { loungeId: result.loungeId } } as any)
    } catch (err: unknown) {
      const isLoungeFull =
        isConvexErrorWithCode(err, LOUNGE_FULL_ERROR_CODE) ||
        (err instanceof Error && err.message.toLowerCase().includes('lounge is full'))
      const isInvalidCode =
        isConvexErrorWithCode(err, INVALID_JOIN_CODE_ERROR_CODE) ||
        (err instanceof Error && err.message.toLowerCase().includes('invalid share code'))
      if (isLoungeFull) {
        setLoungeFullModalVisible(true)
        setError(null)
      } else if (isInvalidCode) {
        setError(translate('loungeJoinScreen.invalidCode'))
      } else {
        setError(translate('lounges.somethingWentWrong'))
      }
    } finally {
      setJoining(false)
    }
  }, [code, userId, userName, isTeacher, isAdmin, joinMutation, router, setEvent])

  const handleChangeCode = useCallback((next: string) => {
    setCode(next)
    setError(null)
  }, [])

  return (
    <View>
      <LoungeCodeCard
        mode="join"
        code={code}
        onChangeCode={handleChangeCode}
        onSubmit={handleJoin}
        submitting={joining}
        error={error}
      />
      <LoungeFullModal visible={loungeFullModalVisible} onDismiss={() => setLoungeFullModalVisible(false)} />
    </View>
  )
}

function CreateALoungeButton() {
  const router = useRouter()
  const {
    analytics: { setEvent },
    session: { user, isTeacher, isAdmin }
  } = useStore()

  return (
    <Pressable
      style={styles.createButton}
      onPress={() => {
        setEvent(events.lounge.create_lounge_tapped, {
          userId: user?.auth0Id ?? '',
          isTeacher,
          isAdmin
        })
        router.push('/lounges/create')
      }}
    >
      <DS.Icon name="Plus--enclosed" size={24} color="Brand/Maroon" />
      <DS.Text variant="Heading 4/Medium" style={[styles.createButtonLabel, { fontSize: 24 }]}>
        {translate('lounges.createALounge')}
      </DS.Text>
      <DS.Icon name="Arrow---right" color="Brand/Dark Grey" size={20} />
    </Pressable>
  )
}

export function LoungeListScreen() {
  const router = useRouter()
  const { session } = useStore()
  const { user } = session
  const userId = user?.auth0Id ?? ''

  const lounges = useQuery(api.lounges.queries.getLoungesForUser, userId ? { userId } : 'skip')

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar style="dark" />

      <View style={styles.header}>
        <Pressable
          onPress={() => (router.canGoBack() ? router.back() : router.replace('/(root)/(tabs)/play' as any))}
          style={styles.headerButton}
        >
          <DS.Icon name="Arrow---left" color="Global/Black" size={24} />
        </Pressable>
        <DS.Text variant="Heading 2/Medium" style={styles.headerTitle}>
          {translate('lounges.explainerTitle')}
        </DS.Text>
        <View style={styles.headerButton} />
      </View>

      <FlatList
        data={(lounges ?? []) as LoungeItem[]}
        keyExtractor={(item) => item._id}
        contentContainerStyle={styles.list}
        renderItem={({ item }) => (
          <LoungeCard
            lounge={item}
            onPress={() => router.push({ pathname: '/lounges/[loungeId]', params: { loungeId: item._id } })}
          />
        )}
        ListEmptyComponent={
          <DS.Text variant="Heading 5/Medium" style={styles.emptyStateText}>
            {translate('lounges.emptyStateDescription')}
          </DS.Text>
        }
        ListFooterComponent={
          <View style={styles.footer}>
            <JoinALoungeSection />
            <CreateALoungeButton />
          </View>
        }
      />
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    height: 54
  },
  headerButton: {
    padding: 4,
    width: 40,
    alignItems: 'center'
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center'
  },
  list: {
    paddingHorizontal: 24,
    paddingTop: 32,
    gap: 8
  },
  loungeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 8,
    padding: 12,
    gap: 12,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  },
  loungeCardDetails: {
    flex: 1,
    minWidth: 0,
    gap: 4
  },
  emptyStateText: {
    textAlign: 'center',
    lineHeight: 30,
    color: DS.palette['Global/Black']
  },
  // Create a Lounge button
  footer: {
    paddingTop: 32,
    paddingBottom: 32
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 40,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginTop: 32,
    gap: 12
  },
  createButtonLabel: {
    flex: 1
  }
})
